import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import { Settings, Shield, Save } from 'lucide-react';

const ReportPermissions = ({ user, apiRequest }) => {
  const [reports, setReports] = useState([]);
  const [teamLevels, setTeamLevels] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [changes, setChanges] = useState({});

  const fetchReportPermissions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiRequest('/api/admin/report-permissions');

      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
        setTeamLevels(data.teamLevels || []);
        setPermissions(data.permissions || {});
        setChanges({});
        setError(null);
      } else {
        setError('Failed to fetch report permissions');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  const handlePermissionChange = (reportId, teamLevel, checked) => {
    const currentPermissions = permissions[reportId] || [];
    const newPermissions = checked
      ? [...currentPermissions, teamLevel]
      : currentPermissions.filter(level => level !== teamLevel);

    setPermissions(prev => ({
      ...prev,
      [reportId]: newPermissions
    }));

    setChanges(prev => ({
      ...prev,
      [reportId]: newPermissions
    }));
  };

  const handleSelectAll = (reportId, checked) => {
    const newPermissions = checked ? [...teamLevels] : [];

    setPermissions(prev => ({
      ...prev,
      [reportId]: newPermissions
    }));

    setChanges(prev => ({
      ...prev,
      [reportId]: newPermissions
    }));
  };

  const savePermissions = async (reportId) => {
    if (!changes[reportId]) return;

    try {
      setSaving(true);
      const res = await apiRequest(`/api/admin/report-permissions/${reportId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allowedTeamLevels: changes[reportId] })
      });

      if (res.ok) {
        setSuccess(`Permissions updated for ${reports.find(r => r.id === reportId)?.name}`);
        // Remove from changes since it's saved
        setChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[reportId];
          return newChanges;
        });

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to update permissions');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setSaving(false);
    }
  };

  const saveAllChanges = async () => {
    const reportIds = Object.keys(changes);
    if (reportIds.length === 0) return;

    setSaving(true);
    let hasError = false;

    for (const reportId of reportIds) {
      try {
        const res = await apiRequest(`/api/admin/report-permissions/${reportId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ allowedTeamLevels: changes[reportId] })
        });

        if (!res.ok) {
          hasError = true;
          break;
        }
      } catch (err) {
        hasError = true;
        break;
      }
    }

    setSaving(false);

    if (!hasError) {
      setSuccess('All permissions updated successfully!');
      setChanges({});
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError('Failed to update some permissions');
    }
  };

  useEffect(() => {
    fetchReportPermissions();
  }, [fetchReportPermissions]);

  if (!user?.isAdmin) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, p: 3 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Box>
    );
  }

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 6, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Shield size={32} style={{ marginRight: '12px' }} />
        <Typography variant="h4" gutterBottom>
          Report Permissions Management
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Configure which team levels have access to each report type. Users can only access reports that are enabled for their team level.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography>
              You have unsaved changes. Click "Save All Changes" to apply them.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save size={16} />}
              onClick={saveAllChanges}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </Box>
        </Alert>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Settings size={24} />
            Report Access by Team Level
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check the boxes to grant access to specific team levels for each report
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Report Name</TableCell>
                  {teamLevels.map((level) => (
                    <TableCell key={level} align="center" sx={{ fontWeight: 'bold' }}>
                      {level}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Select All
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => {
                  const reportPermissions = Array.isArray(permissions[report.id]) ? permissions[report.id] : [];
                  const hasUnsavedChanges = changes[report.id] !== undefined;
                  const allSelected = teamLevels.every(level => reportPermissions.includes(level));
                  const someSelected = teamLevels.some(level => reportPermissions.includes(level));

                  return (
                    <TableRow key={report.id} sx={{ bgcolor: hasUnsavedChanges ? 'action.hover' : 'inherit' }}>
                      <TableCell sx={{ fontWeight: hasUnsavedChanges ? 'bold' : 'normal' }}>
                        <Typography variant="body1">
                          {report.name}
                          {hasUnsavedChanges && (
                            <Typography variant="caption" sx={{ color: 'primary.main', display: 'block' }}>
                              (Modified)
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      {teamLevels.map((level) => (
                        <TableCell key={level} align="center">
                          <Checkbox
                            checked={reportPermissions.includes(level)}
                            onChange={(e) => handlePermissionChange(report.id, level, e.target.checked)}
                            color="primary"
                          />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={(e) => handleSelectAll(report.id, e.target.checked)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {hasUnsavedChanges && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Save size={16} />}
                            onClick={() => savePermissions(report.id)}
                            disabled={saving}
                          >
                            Save
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={fetchReportPermissions}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportPermissions;